import axios from 'axios';
import { API_BASE_URL, getWsUrl } from './config';

class ReservationsWS {
  constructor() {
    this.socket = null;
    this.listeners = new Set();
    this.holdListeners = new Set();
    this.holdSnapshotListeners = new Set();
    this.retryAttempt = 0;
    this.retryTimeoutId = null;
    this.pollIntervalId = null;
    this.subscription = { date: null, resource_id: null };
    this.isExplicitlyClosed = false;
  }

  addListener(callback) {
    this.listeners.add(callback);
    return () => this.removeListener(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  addHoldListener(callback) {
    this.holdListeners.add(callback);
    return () => this.holdListeners.delete(callback);
  }

  addHoldSnapshotListener(callback) {
    this.holdSnapshotListeners.add(callback);
    return () => this.holdSnapshotListeners.delete(callback);
  }

  _emit(reservations, meta = {}) {
    this.listeners.forEach((cb) => {
      try { cb(reservations, meta); } catch (_) {}
    });
  }

  _emitHold(payload) {
    this.holdListeners.forEach((cb) => {
      try { cb(payload); } catch (_) {}
    });
  }

  subscribe(date, resourceId = null) {
    this.subscription = { date, resource_id: resourceId };
    this.isExplicitlyClosed = false;
    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      this._connect();
    } else if (this.socket.readyState === WebSocket.OPEN) {
      this._send({ action: 'subscribe', date, resource_id: resourceId });
    }
  }

  updateSubscription(date, resourceId = null) {
    this.subscription = { date, resource_id: resourceId };
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this._send({ action: 'subscribe', date, resource_id: resourceId });
    }
  }

  refresh() {
    // Ask server to send a fresh snapshot immediately
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this._send({ action: 'refresh' });
    } else {
      // If socket is down, poll once via REST
      this._pollOnce();
    }
  }

  close() {
    this.isExplicitlyClosed = true;
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    this._stopPolling();
    if (this.socket) {
      try { this.socket.close(); } catch (_) {}
      this.socket = null;
    }
  }

  _connect() {
    const token = localStorage.getItem('token');
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    const wsUrl = getWsUrl(`/ws/reservations/${tokenQuery}`);
    const ws = new WebSocket(wsUrl);
    this.socket = ws;

    ws.onopen = () => {
      this.retryAttempt = 0;
      this._stopPolling();
      if (this.subscription.date) {
        this._send({ action: 'subscribe', ...this.subscription });
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'reservations.list' && Array.isArray(data.reservations)) {
          this._emit(data.reservations, { date: data.date, resource_id: data.resource_id });
        } else if (data.type === 'reservations.hold' && data.payload) {
          this._emitHold(data.payload);
        } else if (data.type === 'reservations.holds' && Array.isArray(data.holds)) {
          this.holdSnapshotListeners.forEach((cb) => {
            try { cb(data.holds, { date: data.date, resource_id: data.resource_id }); } catch (_) {}
          });
        }
      } catch (_) {
        // ignore
      }
    };

    ws.onerror = () => {
      // ignore; onclose handles reconnect
    };

    ws.onclose = () => {
      if (this.isExplicitlyClosed) return;
      this._scheduleReconnect();
    };
  }

  sendHold(action, { hold_id, date, resource_id, start_minute, end_minute }) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    const payload = { action, hold_id, date, resource_id, start_minute, end_minute };
    this._send(payload);
  }

  _scheduleReconnect() {
    // start HTTP polling while disconnected
    this._startPolling();

    const attempt = Math.min(this.retryAttempt + 1, 10);
    this.retryAttempt = attempt;
    const backoff = Math.min(30000, 1000 * Math.pow(2, attempt - 1));
    const jitter = Math.floor(Math.random() * 1000);
    const delay = backoff + jitter;

    if (this.retryTimeoutId) clearTimeout(this.retryTimeoutId);
    this.retryTimeoutId = setTimeout(() => {
      // fetch once before reconnect to keep UI fresh
      this._pollOnce();
      this._connect();
    }, delay);
  }

  _send(payload) {
    try {
      this.socket.send(JSON.stringify(payload));
    } catch (_) {}
  }

  _startPolling() {
    if (this.pollIntervalId) return;
    // poll every 15s while WS is down
    this.pollIntervalId = setInterval(() => this._pollOnce(), 15000);
  }

  _stopPolling() {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  async _pollOnce() {
    const { date, resource_id } = this.subscription;
    if (!date) return;
    try {
      const currentDateStr = `${date}-00-00-00`;
      const url = resource_id != null
        ? `${API_BASE_URL}/api/reservations/${currentDateStr}/?resource_id=${resource_id}`
        : `${API_BASE_URL}/api/reservations/${currentDateStr}`;
      const resp = await axios.get(url);
      // REST returns a list already in occurrence shape
      this._emit(resp.data, { date, resource_id });
    } catch (_) {
      // swallow during downtime
    }
  }
}

const reservationsWS = new ReservationsWS();
export default reservationsWS;
